import React, { useEffect } from "react";
import { Layout, Row, Col, Skeleton, Table, Select, Input, DatePicker } from "antd";
import { ApiHelper, getUserData } from "../../../../utils/Helpers";
import { ApiRoutes } from "../../../../utils/Config/ApiRoutes";
import Emptymsg from "../../../layout/message/Emptymsg";
import { messages } from "../../../../utils/Common/Messages";
import Credit from "../../../../assets/images/Wallet/wallet-loaded-01.png";
import Debit from "../../../../assets/images/Wallet/paid-for-order-01.png";
import Cashback from "../../../../assets/images/Wallet/cashback-01.png";
import moment from "moment";
import "./wallet.less";
const { Content } = Layout;
const { RangePicker } = DatePicker;
const { Search } = Input;
const { Option } = Select;

const DATE_FORMAT = "YYYY-MM-DD";
const RESIZE_OBSERVER_ERROR_MESSAGES = [
  "ResizeObserver loop completed with undelivered notifications.",
  "ResizeObserver loop limit exceeded",
];

const installResizeObserverErrorFilter = () => {
  if (typeof window === "undefined" || window.__walletResizeObserverErrorFilterInstalled) {
    return;
  }

  const suppressResizeObserverError = (event) => {
    const message =
      event?.message ||
      event?.error?.message ||
      event?.reason?.message ||
      "";

    if (!RESIZE_OBSERVER_ERROR_MESSAGES.includes(message)) {
      return;
    }

    event.preventDefault?.();
    event.stopImmediatePropagation?.();
  };

  window.addEventListener("error", suppressResizeObserverError, true);
  window.__walletResizeObserverErrorFilterInstalled = true;
};

installResizeObserverErrorFilter();

const getPresetDateRange = (preset) => {
  switch (preset) {
    case "yesterday":
      return [
        moment().subtract(1, "day").startOf("day"),
        moment().subtract(1, "day").endOf("day"),
      ];
    case "last7days":
      return [moment().subtract(6, "days").startOf("day"), moment().endOf("day")];
    case "last30days":
      return [moment().subtract(29, "days").startOf("day"), moment().endOf("day")];
    case "custom":
      return [moment().startOf("day"), moment().endOf("day")];
    case "today":
    default:
      return [moment().startOf("day"), moment().endOf("day")];
  }
};

const normalizeText = (value) => String(value || "").trim().toLowerCase();

const extractCommentValue = (comment, key) => {
  const match = String(comment || "").match(new RegExp(`${key}=([^,\\]]+)`, "i"));
  return match?.[1]?.trim() || "";
};

const getTransactionContent = (record = {}) =>
  [
    record.comments,
    record.finAccountTransTypeDescription,
    record.reasonDescription,
    record.reasonEnumId,
    record.finAccountTransTypeEnumId,
    record.productCategory,
    record.transactionType,
    record.transactionReference,
    record.payRef,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

const numberFormat = (walletAmount) => {
  return Number(parseFloat(walletAmount).toFixed(2)).toLocaleString(
    undefined, // leave undefined to use the visitor's browser
    // locale or a string like 'en-US' to override it.
    { minimumFractionDigits: 2 },
  );
};

const isWalletLoadType = (typeId) =>
  typeId === 5 || typeId === "5" || typeId === 6 || typeId === "6";

const isDebitType = (typeId) => typeId === 7 || typeId === "7";

const isCashbackType = (typeId) => typeId === 8 || typeId === "8";

const getTransactionKind = (record = {}) => {
  if (isWalletLoadType(record.transactionTypeId)) {
    return "credit";
  }
  if (isDebitType(record.transactionTypeId)) {
    return "debit";
  }
  if (isCashbackType(record.transactionTypeId)) {
    return "cashback";
  }

  const finAccountType = String(
    record.finAccountTransTypeEnumId || record.finAccountTransTypeDescription || "",
  ).toLowerCase();
  const reasonType = String(
    record.reasonEnumId || record.reasonDescription || "",
  ).toLowerCase();
  const comments = String(record.comments || "").toLowerCase();

  if (finAccountType.includes("withdraw")) {
    return "debit";
  }
  if (reasonType.includes("refund") || comments.includes("cash back")) {
    return "cashback";
  }
  if (finAccountType.includes("deposit")) {
    return "credit";
  }
  return null;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(Number(value) || value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return moment(date).format("DD-MMM-YYYY HH:mm");
};

const getTransactionIcon = (record) => {
  const transactionKind = getTransactionKind(record);
  if (transactionKind === "credit") {
    return Credit;
  }
  if (transactionKind === "debit") {
    return Debit;
  }
  if (transactionKind === "cashback") {
    return Cashback;
  }
  return null;
};

const getProductCategoryLabel = (record = {}) => {
  const rawCategory =
    record.productCategory ||
    record.category ||
    record.transactionCategory ||
    record.serviceCategory;
  if (rawCategory) {
    const normalized = normalizeText(rawCategory);
    if (normalized.includes("recharge") || normalized.includes("mobile")) {
      return "Mobile Recharge";
    }
    if (normalized.includes("bill")) {
      return "Bill Payment";
    }
    return rawCategory;
  }

  const content = getTransactionContent(record);
  if (
    content.includes("recharge mobile") ||
    content.includes("mobile recharge") ||
    content.includes("carrierid=") ||
    content.includes("planid=")
  ) {
    return "Mobile Recharge";
  }
  if (
    content.includes("bill payment") ||
    content.includes("billpay") ||
    content.includes("billerid=")
  ) {
    return "Bill Payment";
  }
  return "";
};

const getCarrierOrBillerLabel = (record = {}) => {
  const providerName =
    record.carrierName ||
    record.billerName ||
    record.providerName ||
    record.productProviderName;
  if (providerName) {
    return providerName;
  }

  const carrierId = extractCommentValue(record.comments, "carrierId");
  if (carrierId) {
    return `Carrier ID: ${carrierId}`;
  }

  const billerId = extractCommentValue(record.comments, "billerId");
  if (billerId) {
    return `Biller ID: ${billerId}`;
  }

  return "";
};

const isWalletAdjustmentRecord = (record = {}) => {
  const reasonEnumId = String(record.reasonEnumId || "").toLowerCase();
  const reasonDescription = String(record.reasonDescription || "").toLowerCase();
  const comments = String(record.comments || "").toLowerCase();

  return (
    reasonEnumId === "fatrcscredit" ||
    reasonDescription.includes("customer service credit") ||
    comments.includes("wallet balance reload")
  );
};

const Transactionsummary = () => {
  const {
    currencySign,
    parentOrganizationId,
    organizationId,
    language,
    organizationTypeId,
    time_gmt,
  } = getUserData();
  const lan = language ?? "EN";
  const [perPage, setPerPage] = React.useState(10);
  const [page, setPage] = React.useState(1);
  const [data, setData] = React.useState([]);
  const [totalReacord, setTotalReacord] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [datePreset, setDatePreset] = React.useState("today");
  const [customDateRange, setCustomDateRange] = React.useState(getPresetDateRange("today"));
  const [searchValue, setSearchValue] = React.useState("");
  const [filters, setFilters] = React.useState({
    startDate: getPresetDateRange("today")[0].format(DATE_FORMAT),
    endDate: getPresetDateRange("today")[1].format(DATE_FORMAT),
    reasonEnumId: "all",
    searchKey: "",
  });

  const getTransactionLabel = (record) => {
    const transactionKind = getTransactionKind(record);
    const category = getProductCategoryLabel(record);
    const content = getTransactionContent(record);
    const isVoid = content.includes("void") || content.includes("reversal");
    const isAdjustment = content.includes("adjustment");
    const isWalletAdjustment = isWalletAdjustmentRecord(record);

    if (isWalletAdjustment) {
      return "Wallet Adjustment";
    }
    if (isWalletLoadType(record.transactionTypeId) || Number(record.chargedToCardAmount || 0) > 0) {
      return "Wallet Loaded";
    }
    if (isAdjustment && transactionKind === "cashback") {
      return "Cashback Adjustment";
    }
    if (isAdjustment) {
      return "Wallet Adjustment";
    }
    if (transactionKind === "cashback") {
      if (isVoid && category === "Bill Payment") {
        return "Cashback Reversal (Bill Payment Void)";
      }
      if (category === "Mobile Recharge") {
        return "Cashback - Mobile Recharge";
      }
      if (category === "Bill Payment") {
        return "Cashback - Bill Payment";
      }
      return "Cashback";
    }
    if (transactionKind === "debit") {
      if (isVoid && category === "Bill Payment") {
        return "Bill Payment Void";
      }
      if (category === "Mobile Recharge") {
        return "Paid for Mobile Recharge";
      }
      if (category === "Bill Payment") {
        return "Paid for Bill Payment";
      }
      return "Wallet Debit";
    }
    if (record.finAccountTransTypeDescription || record.reasonDescription) {
      return [record.finAccountTransTypeDescription, record.reasonDescription]
        .filter(Boolean)
        .join(" - ");
    }
    return "-";
  };

  const getTransactionReferenceValue = (record) =>
    record.transactionReference ||
    record.reference ||
    record.finAccountTransId ||
    record.requestId ||
    record.walletFundingReference ||
    record.payRef ||
    record.orderId ||
    record.walletTransactionHistoryId ||
    record.organizationPartyId ||
    "-";

  const getRelatedOrderTransactionValue = (record) => {
    if (record.relatedOrderTransaction) {
      return record.relatedOrderTransaction;
    }
    const orderId = record.pointepayOrderId || record.orderId;
    if (orderId && orderId !== 0 && orderId !== "0") {
      return `Order #${orderId}`;
    }
    const transactionId = String(record.comments || "").match(/transaction\s*\[(.*?)\]/i)?.[1];
    if (transactionId) {
      return `Txn #${transactionId}`;
    }
    return record.comments || "-";
  };

  const renderWalletMovement = (amount, record) => {
    const transactionKind = getTransactionKind(record);
    const numericAmount = Number(amount || 0);
    if (Number.isNaN(numericAmount) || numericAmount === 0) {
      return "-";
    }
    const formattedAmount = `${currencySign}${numberFormat(Math.abs(numericAmount))}`;

    if (transactionKind === "debit") {
      return (
        <span className="fw-600 minus">{`-${formattedAmount}`}</span>
      );
    }
    if (transactionKind === "credit" || transactionKind === "cashback") {
      return (
        <span className="fw-600 plus">{`+${formattedAmount}`}</span>
      );
    }
    return "-";
  };

  const renderCurrencyValue = (amount) => {
    const numericAmount = Number(amount);
    if (Number.isNaN(numericAmount)) {
      return "-";
    }
    return `${currencySign}${numberFormat(Math.abs(numericAmount))}`;
  };

  useEffect(() => {
    getWalletHistroy(perPage, page);
    // eslint-disable-next-line
  }, [perPage, page, refreshKey, filters]);

  useEffect(() => {
    const handleWalletHistoryRefresh = () => {
      setRefreshKey((prev) => prev + 1);
    };
    window.addEventListener(
      "wallet:history:refresh",
      handleWalletHistoryRefresh,
    );
    return () => {
      window.removeEventListener(
        "wallet:history:refresh",
        handleWalletHistoryRefresh,
      );
    };
  }, []);

  const nextData = (pagination) => {
    setPage(pagination.current);
    setPerPage(pagination.pageSize);
    //searchData(pagination.pageSize, pagination.current);
  };

  const updateFilters = (nextFilterValues) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, ...nextFilterValues }));
  };

  const handleDatePresetChange = (value) => {
    setDatePreset(value);
    if (value === "custom") {
      return;
    }

    const nextRange = getPresetDateRange(value);
    setCustomDateRange(nextRange);
    updateFilters({
      startDate: nextRange[0].format(DATE_FORMAT),
      endDate: nextRange[1].format(DATE_FORMAT),
    });
  };

  const handleCustomDateChange = (dates) => {
    setCustomDateRange(dates);
    if (dates?.length === 2 && dates[0] && dates[1]) {
      updateFilters({
        startDate: dates[0].format(DATE_FORMAT),
        endDate: dates[1].format(DATE_FORMAT),
      });
    }
  };

  const handleSearch = (value) => {
    updateFilters({
      searchKey: value?.trim() || "",
    });
  };

  const getWalletHistroy = async (perPage, page) => {
    setLoading(true);
    setData([]);
    setTotalReacord(0);
    const requestBody = {
      parentOrganizationId: parentOrganizationId,
      organizationId: organizationId,
      organizationTypeId,
      gmtTime: time_gmt,
      startDate: filters.startDate,
      endDate: filters.endDate,
      lan,
    };

    if (filters.searchKey) {
      requestBody.filter = filters.searchKey;
    }
    if (filters.reasonEnumId !== "all") {
      requestBody.reasonEnumId = filters.reasonEnumId;
    }

    const response = await new ApiHelper().FetchFromServer(
      ApiRoutes.WALLET_HISTORY_CELLPAY.service,
      ApiRoutes.WALLET_HISTORY_CELLPAY.url,
      ApiRoutes.WALLET_HISTORY_CELLPAY.method,
      ApiRoutes.WALLET_HISTORY_CELLPAY.authenticate,
      {
        pageSize: perPage,
        pageIndex: Math.max(page - 1, 0),
      },
      requestBody,
    );
    if (response.code === 200) {
      const payload = response.data || {};
      const walletHistory =
        payload?.data && !Array.isArray(payload.data) ? payload.data : payload;
      const transactionList = Array.isArray(walletHistory?.transactionList)
        ? walletHistory.transactionList
        : Array.isArray(walletHistory?.data)
          ? walletHistory.data
          : [];
      const totalRows =
        walletHistory?.totalRows ??
        walletHistory?.total ??
        transactionList.length;

      if (transactionList.length || totalRows) {
        setData(transactionList);
        setTotalReacord(totalRows);
        setLoading(false);
      } else {
        setData([]);
        setTotalReacord(0);
        setLoading(false);
        //  setNoDatAvailable(true);
      }
    } else {
      setData([]);
      setTotalReacord(0);
      setLoading(false);
    }
  };
  const columns =
    // organizationTypeId === 1 || organizationTypeId === "1"
    //   ? [
    //       {
    //         title: "",
    //         dataIndex: "transactionTypeId",
    //         key: "transactionTypeId",
    //         width: "0px",
    //         className: "pr-0",
    //         render: (text) => {
    //           return isWalletLoadType(text) ? (
    //             <img src={Credit} alt="Icon" width="46" />
    //           ) : isDebitType(text) ? (
    //             <img src={Debit} alt="Icon" width="46" />
    //           ) : isCashbackType(text) ? (
    //             <img src={Cashback} alt="Icon" width="46" />
    //           ) : (
    //             "-"
    //           );
    //         },
    //       },
    //       {
    //         title: messages.transactionType[lan],
    //         dataIndex: "pointepayOrderId",
    //         key: "pointepayOrderId",
    //         render: (text, record) => {
    //           const refData = getTransactionRef(record);
    //           return (
    //             <>
    //               <h3 className="mb-0">{getTransactionLabel(record)}</h3>
    //               {refData ? (
    //                 <p className="mb-0 fs-08">
    //                   <span className="orderLabel">{refData.label}:</span>{" "}
    //                   {refData.value}
    //                 </p>
    //               ) : (
    //                 ""
    //               )}
    //             </>
    //           );
    //         },
    //       },
    //       {
    //         title: messages.dateTime[lan],
    //         dataIndex: "createDt",
    //         key: "createDt",
    //         // render: (createDt) => <>{moment(createDt, "DD MM YYYY hh:mm:ss")}</>,
    //       },
    //       {
    //         title: messages.amount[lan],
    //         key: "amount",
    //         dataIndex: "amount",
    //         render: (amount, record) => <>{renderAmount(amount, record)}</>,
    //       },
    //     ]
    [
      {
        title: "Organization Name",
        dataIndex: "organizationName",
        key: "organizationName",
        render: (text, record) =>
          text ||
          record.dbaName ||
          record.fromPartyName ||
          record.toPartyName ||
          record.organizationPartyId ||
          "-",
      },
      {
        title: "Transaction Type",
        dataIndex: "pointepayOrderId",
        key: "pointepayOrderId",
        render: (_, record) => {
          const transactionIcon = getTransactionIcon(record);
          return (
            <div className="displayFlex alignCenter">
              {transactionIcon ? (
                <img src={transactionIcon} alt="Icon" width="36" className="mr-1" />
              ) : null}
              <div>
                <div>{getTransactionLabel(record)}</div>
              </div>
            </div>
          );
        },
      },
      {
        title: "Transaction Reference",
        dataIndex: "transactionReference",
        key: "transactionReference",
        render: (_, record) => {
          const productCategory = getProductCategoryLabel(record);
          const carrierOrBiller = getCarrierOrBillerLabel(record);
          return (
            <>
              <div>{getTransactionReferenceValue(record)}</div>
              {productCategory ? <div className="transactionMeta">{productCategory}</div> : null}
              {carrierOrBiller ? <div className="transactionMeta">{carrierOrBiller}</div> : null}
            </>
          );
        },
      },
      {
        title: "Related Order / Transaction",
        dataIndex: "relatedOrderTransaction",
        key: "relatedOrderTransaction",
        render: (_, record) => <>{getRelatedOrderTransactionValue(record)}</>,
      },
      {
        title: "Date & Time",
        dataIndex: "createDt",
        key: "createDt",
        render: (text, record) => <>{formatDateTime(text || record.entryDate || record.transactionDate)}</>,
      },
      {
        title: "Wallet Movement",
        key: "amount",
        dataIndex: "amount",
        render: (amount, record) => <>{renderWalletMovement(amount, record)}</>,
      },
      {
        title: "Total Charged to Card",
        key: "Total Charged to Card",
        dataIndex: "chargedToCardAmount",
        render: (chargedToCardAmount) => {
          // if (Number(chargedToCardAmount) === 0) {
          //   return <>-</>;
          // }
          return <>{renderCurrencyValue(chargedToCardAmount)}</>;
        },
      },
      {
        title: "Card Processing Fee",
        key: "Fee",
        dataIndex: "feeAmount",
        render: (amount) => {
          if (Number(amount) === 0) {
            return <>-</>;
          }
          return <>{renderCurrencyValue(amount)}</>;
        },
      },
      {
        title: "Wallet Loaded Amount",
        key: "amount",
        dataIndex: "amount",
        render: (amount, record) => <>{renderWalletMovement(amount, record)}</>,
      },
      {
        title: "Running Balance",
        dataIndex: "walletBalance",
        key: "walletBalance",
        align: "right",
        className: "fw-700",
        render: (amount, record) => {
          const balanceAmount = amount ?? record.postBalance;
          return balanceAmount || balanceAmount === 0 ? (
            <span className="fw-700">{`${currencySign}${numberFormat(balanceAmount)}`}</span>
          ) : (
            "-"
          );
        },
      },
    ];
  return (
    <>
      <Content className="content-body" style={{ minHeight: 200 }}>
        <Row>
          <Col lg={24} xs={24}>
            <div
              className="bg-white"
              style={{
                margin: "14px 14px 0px 14px",
                padding: 14,
                minHeight: 200,
              }}
            >
              <h4 className="text-uppercase">
                {messages.transactionSummary[lan]}
              </h4>
              <Row gutter={[12, 12]} className="mb-1">
                <Col xl={4} lg={6} md={8} xs={24}>
                  <Select
                    value={datePreset}
                    className="w-100"
                    onChange={handleDatePresetChange}
                  >
                    <Option value="today">Today</Option>
                    <Option value="yesterday">Yesterday</Option>
                    <Option value="last7days">Last 7 Days</Option>
                    <Option value="last30days">Last 30 Days</Option>
                    <Option value="custom">Custom Date Range</Option>
                  </Select>
                </Col>
                {datePreset === "custom" ? (
                  <Col xl={6} lg={8} md={10} xs={24}>
                    <RangePicker
                      value={customDateRange}
                      onChange={handleCustomDateChange}
                      allowClear={false}
                      format={DATE_FORMAT}
                    />
                  </Col>
                ) : null}
                <Col xl={4} lg={6} md={8} xs={24}>
                  <Select
                    value={filters.reasonEnumId}
                    className="w-100"
                    onChange={(value) => updateFilters({ reasonEnumId: value })}
                  >
                    <Option value="all">All Transaction Types</Option>
                    <Option value="FatrBillPay">BillPay</Option>
                    <Option value="FatrMobRech">Recharge</Option>
                    <Option value="FatrCashBack">Cashback</Option>
                    <Option value="FatrWalletLoad">WalletLoad</Option>
                    <Option value="FatrVoid">Void</Option>
                  </Select>
                </Col>
                <Col xl={8} lg={10} md={12} xs={24}>
                  <Search
                    placeholder="Search by Order ID or Transaction Reference"
                    allowClear
                    value={searchValue}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      setSearchValue(nextValue);
                      if (!nextValue) {
                        handleSearch("");
                      }
                    }}
                    onSearch={(value) => {
                      setSearchValue(value);
                      handleSearch(value);
                    }}
                  />
                </Col>
              </Row>
              <Table
                columns={columns}
                rowKey={(record, index) =>
                  record.finAccountTransId ||
                  record.walletTransactionHistoryId ||
                  record.orderId ||
                  `${record.organizationPartyId || "wallet"}-${record.entryDate || index}`
                }
                dataSource={data}
                className="walletTable"
                pageIndex={page}
                scroll={{ x: 1500 }}
                pagination={
                  parseInt(totalReacord) > perPage
                    ? {
                      current: page,
                      pageSize: perPage,
                      total: totalReacord,
                      pageSizeOptions: ["10", "20", "50"],
                      showSizeChanger: true,
                    }
                    : false
                }
                locale={{
                  emptyText: loading ? (
                    <Skeleton
                      paragraph={{ rows: 5 }}
                      loading={loading}
                      active
                    ></Skeleton>
                  ) : (
                    <Emptymsg emptytitle={messages.emptyTitleWallet[lan]} />
                  ),
                }}
                onChange={nextData}
              />
              {/* <Table
                  dataSource={data}
                  columns={columns}
                  pagination={false}
                  className="walletTable"
                /> */}
            </div>
          </Col>
        </Row>
      </Content>
    </>
  );
};

export default Transactionsummary;
